import React, { useState, useEffect } from 'react';
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
    Container, Grid, Card, CardContent, Typography, CircularProgress, Alert,
    CardActionArea, Box, IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle, Button
} from '@mui/material';
import JobHistoryModal from './JobHistoryModal';
import EditScheduledJobModal from './EditScheduledJobModal';
import { getScheduledJobs, deleteScheduledJob, updateScheduledJob } from '../../Utils/functions';
import { DeleteOutline as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

const ScheduledJobs = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const [scheduledJobs, setScheduledJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) {
            return;
        }
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const data = await getScheduledJobs();
            setScheduledJobs(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (job) => {
        setSelectedJob(job);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedJob(null);
    };

    const handleEditClick = (job) => {
        setSelectedJob(job);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedJob(null);
    };

    const handleDeleteClick = (job) => {
        setJobToDelete(job);
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteScheduledJob(jobToDelete.id);
            setScheduledJobs(scheduledJobs.filter(j => j.id !== jobToDelete.id));
            setJobToDelete(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleUpdateJob = async (updatedJob) => {
        try {
            const data = await updateScheduledJob(updatedJob.id, updatedJob);
            setScheduledJobs(scheduledJobs.map(j => j.id === updatedJob.id ? data : j));
            handleCloseEditModal();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) {
        return (
            <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Your Scheduled Searches
            </Typography>
            {scheduledJobs.length === 0 ? (
                <Typography>You have no scheduled searches.</Typography>
            ) : (
                <Grid container spacing={1}>
                    {scheduledJobs.map((job) => (
                        <Grid item key={job.id} xs={12}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardActionArea onClick={() => handleCardClick(job)} sx={{ flexGrow: 1 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0, margin: 0 }}>
                                            <Typography gutterBottom variant="h6" component="h6" noWrap>
                                                {job.question}
                                            </Typography>
                                            <Box sx={{ display: 'flex', p: 0 }}>
                                                <Tooltip title="Edit">
                                                    <IconButton onClick={() => handleEditClick(job)}>
                                                        <EditIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton onClick={() => handleDeleteClick(job)}>
                                                        <DeleteIcon color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                        <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', p: 0 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Next Run:</strong> {new Date(job.nextRunAt).toLocaleDateString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Search Mode:</strong> {job.searchMode}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Frequency:</strong> {job.frequency}
                                            </Typography>
                                        </Box>
                                        {job.query && (
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                <strong>Query:</strong> {job.query}
                                            </Typography>
                                        )}
                                        {job.pico && typeof job.pico === 'object' && (
                                            <>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    <strong>Population:</strong> {job.pico.P}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    <strong>Intervention:</strong> {job.pico.I}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    <strong>Comparison:</strong> {job.pico.C}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    <strong>Outcome:</strong> {job.pico.O}
                                                </Typography>
                                            </>
                                        )}
                                        {job.filters && typeof job.filters === 'object' && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 0 }}>
                                                {Object.entries(job.filters)
                                                    .filter(([key, value]) => value && (Array.isArray(value) ? value.length > 0 : true))
                                                    .map(([key, value]) => (
                                                        <Typography key={key} variant="body2" color="text.secondary" noWrap>
                                                            <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {Array.isArray(value) ? value.join(', ') : value}
                                                        </Typography>
                                                    ))}
                                            </Box>
                                        )}
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
            {isModalOpen && selectedJob && (
                <JobHistoryModal
                    job={selectedJob}
                    onClose={handleCloseModal}
                />
            )}
            {isEditModalOpen && selectedJob && (
                <EditScheduledJobModal
                    job={selectedJob}
                    onClose={handleCloseEditModal}
                    onSave={handleUpdateJob}
                />
            )}
            <Dialog
                open={!!jobToDelete}
                onClose={() => setJobToDelete(null)}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this scheduled search?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setJobToDelete(null)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ScheduledJobs;